import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, useDragControls, useMotionValue } from "motion/react";
import {
  TableCellsIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  CheckIcon,
  ChartBarIcon,
  ArrowsRightLeftIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import {
  TableData,
  formatCellValue,
  exportTableAsCSV,
  exportTableAsXLSX,
  copyTableForExcel,
  isSpaceNumberField,
  isTimeSeriesField,
  getUnderOverColorClass,
  Visualization,
} from "../../types/table";
import ChartVisualization from "../ChartVisualization";
import Sparkline from "../Sparkline";
import EventSystem from "../../../../events/EventSystem";

interface TableModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableData: TableData;
  title?: string;
  onRowClick?: (rowData: Record<string, any>) => void;
  visualizations?: Visualization[];
  mallName?: string;
  currentPlanName?: string;
  currentPropertyNumber?: string;
  onSwitchPlan?: (planName: string, propertyNumber?: string) => void;
  onHighlightAll?: () => void;
  isHighlightActive?: boolean;
}

const TableModal: React.FC<TableModalProps> = ({
  isOpen,
  onClose,
  tableData,
  title = "Data Table",
  onRowClick,
  visualizations = [],
  mallName,
  currentPlanName,
  currentPropertyNumber,
  onSwitchPlan,
  onHighlightAll,
  isHighlightActive = false,
}) => {
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [activeTab, setActiveTab] = useState<"table" | "charts">("table");
  
  // Drag and resize state
  const [modalWidth, setModalWidth] = useState(1200);
  const [modalHeight, setModalHeight] = useState(700);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const initialMousePos = useRef({ x: 0, y: 0 });
  const initialSize = useRef({ width: 0, height: 0 });
  const initialPosition = useRef({ x: 0, y: 0 });
  
  // Motion values for position
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  // Drag controls
  const dragControls = useDragControls();

  const hasVisualizations = visualizations && visualizations.length > 0;

  const { labels, data } = tableData;
  const fieldNames = Object.keys(labels);

  // Check if table has space_number field for click interactions
  const hasSpaceNumberField = fieldNames.some((field) =>
    isSpaceNumberField(field)
  );
  const spaceNumberFieldName = fieldNames.find((field) =>
    isSpaceNumberField(field)
  );

  // Handle CSV export
  const handleExportCSV = () => {
    const csvContent = exportTableAsCSV(tableData, mallName);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const filename = mallName 
      ? `${mallName.replace(/\s+/g, "_")}_${title.replace(/\s+/g, "_")}_export.csv`
      : `${title.replace(/\s+/g, "_")}_export.csv`;
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Excel export as native .xlsx file
  const handleExportExcel = async () => {
    const filename = mallName
      ? `${mallName.replace(/\s+/g, "_")}_${title.replace(/\s+/g, "_")}_export.xlsx`
      : `${title.replace(/\s+/g, "_")}_export.xlsx`;
    await exportTableAsXLSX(tableData, filename, mallName);
  };

  // Handle copy to clipboard for Excel paste
  const handleCopyForExcel = async () => {
    try {
      const tsvContent = copyTableForExcel(tableData, mallName);
      await navigator.clipboard.writeText(tsvContent);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      alert("Failed to copy to clipboard. Please try again.");
    }
  };

  // Handle row click
  const handleRowClick = (row: Record<string, any>) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  // Handle resize start
  const handleResizeStart = (direction: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeDirection(direction);
    initialMousePos.current = { x: e.clientX, y: e.clientY };
    initialSize.current = { width: modalWidth, height: modalHeight };
    initialPosition.current = { x: x.get(), y: y.get() };
    EventSystem.publish("cadviewer.is_dragging_pane", true);
  };

  // Resize handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeDirection) return;

      const deltaX = e.clientX - initialMousePos.current.x;
      const deltaY = e.clientY - initialMousePos.current.y;

      let newWidth = modalWidth;
      let newHeight = modalHeight;
      let newX = x.get();
      let newY = y.get();

      // Handle horizontal resizing
      if (resizeDirection.includes('left')) {
        newWidth = initialSize.current.width - deltaX;
        newX = initialPosition.current.x + deltaX;
      } else if (resizeDirection.includes('right')) {
        newWidth = initialSize.current.width + deltaX;
      }

      // Handle vertical resizing
      if (resizeDirection.includes('top')) {
        newHeight = initialSize.current.height - deltaY;
        newY = initialPosition.current.y + deltaY;
      } else if (resizeDirection.includes('bottom')) {
        newHeight = initialSize.current.height + deltaY;
      }

      // Apply max constraints only (no min constraints)
      newWidth = Math.min(newWidth, window.innerWidth - 40);
      newHeight = Math.min(newHeight, window.innerHeight - 40);

      setModalWidth(newWidth);
      setModalHeight(newHeight);

      if (resizeDirection.includes('left') || resizeDirection.includes('top')) {
        x.set(newX);
        y.set(newY);
      }
    };

    const handleMouseUp = () => {
      setResizeDirection(null);
      EventSystem.publish("cadviewer.is_dragging_pane", false);
    };

    if (resizeDirection) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizeDirection, modalWidth, modalHeight, x, y]);

  // Reset position when modal opens
  useEffect(() => {
    if (isOpen) {
      x.set(0);
      y.set(0);
    }
  }, [isOpen, x, y]);

  // Drag constraints - prevent dragging off screen
  const dragConstraints = useMemo(() => {
    const marginX = Math.max(0, (window.innerWidth - modalWidth) / 2);
    const marginY = Math.max(0, (window.innerHeight - modalHeight) / 2);
    
    return {
      left: -marginX,
      right: marginX,
      top: -marginY,
      bottom: marginY,
    };
  }, [modalWidth, modalHeight]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000000003] w-full h-full" style={{ pointerEvents: 'none' }}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" style={{ pointerEvents: 'none' }}>
        {/* Backdrop */}
        {/* <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div> */}

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal - now draggable and resizable */}
        <motion.div
          ref={modalRef}
          drag
          dragListener={false}
          dragControls={dragControls}
          onDragStart={() => EventSystem.publish("cadviewer.is_dragging_pane", true)}
          onDragEnd={() => EventSystem.publish("cadviewer.is_dragging_pane", false)}
          dragConstraints={dragConstraints}
          dragElastic={0}
          dragMomentum={false}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginLeft: -(modalWidth / 2),
            marginTop: -(modalHeight / 2),
            x,
            y,
            width: modalWidth,
            height: modalHeight,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40,
            pointerEvents: 'auto',
          }}
          className="bg-white rounded-lg text-left overflow-hidden shadow-xl sm:my-8 relative flex flex-col"
        >
          {/* Resize Handles - 4 sides */}

          {/* Top */}
          <div
            onMouseDown={(e) => handleResizeStart('top', e)}
            style={{
              position: 'absolute',
              left: 8,
              right: 8,
              top: 0,
              height: 8,
              cursor: 'ns-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal height from top"
          />

          {/* Right */}
          <div
            onMouseDown={(e) => handleResizeStart('right', e)}
            style={{
              position: 'absolute',
              right: 0,
              top: 8,
              bottom: 8,
              width: 8,
              cursor: 'ew-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal width from right"
          />

          {/* Bottom */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom', e)}
            style={{
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 0,
              height: 8,
              cursor: 'ns-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal height from bottom"
          />

          {/* Left */}
          <div
            onMouseDown={(e) => handleResizeStart('left', e)}
            style={{
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 8,
              cursor: 'ew-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal width from left"
          />

          {/* Resize Handles - 4 corners */}

          {/* Top-Left Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('top-left', e)}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from top-left corner"
          />

          {/* Top-Right Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('top-right', e)}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 16,
              height: 16,
              cursor: 'nesw-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from top-right corner"
          />

          {/* Bottom-Left Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom-left', e)}
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nesw-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from bottom-left corner"
          />

          {/* Bottom-Right Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom-right', e)}
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from bottom-right corner"
          />
          {/* Header - Draggable - Fixed */}
          <div
            className="flex-none px-6 py-4 border-b cursor-move select-none"
            style={{
              background: "linear-gradient(to right, #0f3557, #0a2338)",
              borderBottomColor: "#072033",
              userSelect: "none",
            }}
            onPointerDown={(e) => dragControls.start(e)}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl leading-6 font-bold text-white flex items-center gap-2">
                <TableCellsIcon className="h-6 w-6" />
                {title}
              </h3>
              <div className="flex items-center gap-2">
                {/* Highlight All button - only show when there are spaces to highlight */}
                {hasSpaceNumberField && onHighlightAll && (
                  <button
                    onClick={onHighlightAll}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium transition-colors ${
                      isHighlightActive
                        ? "bg-red-500 hover:bg-red-600"
                        : "bg-green-500 hover:bg-green-600"
                    }`}
                    title={isHighlightActive ? "Clear highlights" : "Highlight all spaces on plan"}
                  >
                    {isHighlightActive ? (
                       <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                        </svg>
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                    {isHighlightActive ? "Clear Highlights" : "Highlight All"}
                  </button>
                )}
                {/* Export buttons */}
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Export as CSV"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  CSV
                </button>
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Export as Excel"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Excel
                </button>
                <button
                  onClick={handleCopyForExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Copy for Excel paste"
                >
                  {copiedToClipboard ? (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="text-white transition-colors ml-2"
                  style={{ opacity: 0.9 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.9";
                  }}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <p
              className="mt-1 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {data.length} row{data.length !== 1 ? "s" : ""} ·{" "}
              {fieldNames.length} column
              {fieldNames.length !== 1 ? "s" : ""}
              {hasSpaceNumberField && (
                <span className="ml-2 text-xs">
                  (Click rows to highlight on plan)
                </span>
              )}
            </p>
          </div>

          {/* Tabs - only show if visualizations exist - Fixed */}
          {hasVisualizations && (
            <div className="flex-none border-b border-gray-200 bg-gray-50">
              <div className="flex gap-1 px-6">
                <button
                  onClick={() => setActiveTab("table")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === "table"
                      ? "border-[#0f3557] text-[#0f3557]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <TableCellsIcon className="h-4 w-4" />
                  Table
                </button>
                <button
                  onClick={() => setActiveTab("charts")}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                    activeTab === "charts"
                      ? "border-[#0f3557] text-[#0f3557]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <ChartBarIcon className="h-4 w-4" />
                  Charts ({visualizations.length})
                </button>
              </div>
            </div>
          )}

          {/* Content based on active tab - Scrollable */}
          <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
          {activeTab === "table" ? (
            /* Table Content */
            <div className="px-6 py-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {fieldNames.map((fieldName) => (
                      <th
                        key={fieldName}
                        className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-50"
                      >
                        {labels[fieldName]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((row, rowIndex) => {
                    const isClickable =
                      hasSpaceNumberField &&
                      spaceNumberFieldName &&
                      row[spaceNumberFieldName];
                    
                    // Check if this row has a different plan_name
                    const rowPlanName = row.plan_name;
                    const isDifferentPlan = rowPlanName && rowPlanName !== currentPlanName;
                    
                    // Check if this row has a different property_number
                    const rowPropertyNumber = row.property_number;
                    const isDifferentProperty = rowPropertyNumber && rowPropertyNumber !== currentPropertyNumber;

                    return (
                      <tr
                        key={rowIndex}
                        onClick={() => isClickable && handleRowClick(row)}
                        className={`${
                          isClickable
                            ? "cursor-pointer hover:bg-blue-50 transition-colors"
                            : ""
                        }`}
                      >
                        {fieldNames.map((fieldName) => {
                          const value = row[fieldName];
                          const isTimeSeries = isTimeSeriesField(fieldName) && Array.isArray(value);
                          const formattedValue = isTimeSeries
                            ? ""
                            : formatCellValue(fieldName, value);
                          const isSpaceNumber = isSpaceNumberField(fieldName);
                          const isPlanField = fieldName === 'plan_name';
                          const showSwitchButton = isPlanField && onSwitchPlan && (isDifferentPlan || isDifferentProperty);

                          const underOverClass = getUnderOverColorClass(value);

                          return (
                            <td
                              key={fieldName}
                              className={`px-4 py-3 text-sm border-r border-gray-200 ${
                                isSpaceNumber
                                  ? "font-semibold text-blue-600"
                                  : isPlanField && isDifferentProperty
                                  ? "text-purple-600 font-medium"
                                  : isPlanField && isDifferentPlan
                                  ? "text-orange-600 font-medium"
                                  : underOverClass
                                  ? underOverClass
                                  : "text-gray-700"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {isTimeSeries ? (
                                  <Sparkline points={value} width={160} height={36} />
                                ) : (
                                  <span>{formattedValue === 'N/A' ? '' : formattedValue}</span>
                                )}
                                {/* Switch Plan button inline with plan_name */}
                                {showSwitchButton && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onSwitchPlan(rowPlanName, rowPropertyNumber);
                                    }}
                                    className={`flex items-center gap-1 p-1.5 rounded-md transition-colors ${
                                      isDifferentProperty
                                        ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-700'
                                        : 'bg-orange-100 text-orange-600 hover:bg-orange-200 hover:text-orange-700'
                                    }`}
                                    title={
                                      isDifferentProperty
                                        ? `Switch to property: ${rowPropertyNumber} / plan: ${rowPlanName}`
                                        : `Switch to plan: ${rowPlanName}`
                                    }
                                  >
                                    <ArrowsRightLeftIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </div>
          ) : (
            /* Charts Content */
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {visualizations.map((visualization, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                  >
                    <ChartVisualization visualization={visualization} />
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>

          {/* Footer - Fixed */}
          <div className="flex-none bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-xs text-gray-500">
              {copiedToClipboard && (
                <span className="text-green-600 font-medium">
                  ✓ Copied to clipboard! You can now paste into Excel.
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm"
              style={{ backgroundColor: "#0f3557" }}
            >
              Close
            </button>
          </div>
      </motion.div>
        </div>
    </div>,
    document.body
  );
};

export default TableModal;
